import debug from 'debug'
import events from './events'
import API from '@/api'
import router from '@/router'
import Session from '@/api/session'
import * as RMQClient from '@/rmqclient'
import BrowserMedia from 'Store/sipua/browser_media'
import EmbeddedLocalStorage from '@/api/embedded'

const dbg = debug('store:action:')

export default {
  authSet ({ commit }) {
    commit('AUTH_SET', true)
  },

  authReset ({ commit }) {
    commit('AUTH_SET', false)
  },

  setCallPanelInitialized ({ commit }) {
    commit('CALL_PANEL_INITIALIZED', true)
  },

  browderApp ({ dispatch, getters }) {
    return Promise.all([
      dispatch('licenses/get'),
      (getters['permissions/confRead'] && dispatch('initRMQ')),
      (getters['permissions/confRead'] && dispatch('initSIPUA')),
      (getters['permissions/parkRead'] && dispatch('parking/get')),
      (getters['permissions/usersRead'] && dispatch('users/initUsers')),
      (getters['permissions/usersRead'] && dispatch('users/initBuddies')),
      (getters['permissions/chatsRead'] && dispatch('chat/getUsers')),
      (getters['permissions/queuesRead'] && dispatch('queues/init')),
      (getters['permissions/conferencesRead'] && dispatch('conferences/init')),
      (getters['permissions/videoConfRead'] && dispatch('videoConferenceConfig/getConfigurations')),
      (getters['permissions/vmailsRead'] && dispatch('voicemails/init')),
      (getters['permissions/toggleRead'] && dispatch('settings/initToggle')),
      (getters['permissions/cdrsRead'] && dispatch('cdrs/getWatchedUsers'))
    ])
  },

  appInit ({ dispatch }) {
    dbg('Init application')
    return API.permissions().then(response => {
      events.mask('Checking permissions...')
      return dispatch('initPermissions', response.data).then(() => {
        if (router.currentRoute.name === '403') {
          router.push('/')
          return
        }
        events.mask('Loading callpanel user...')
        return dispatch('currentUser/getCurrentUser')
      })
    }).then(() => {
      events.mask('Loading application...')
      return dispatch('browderApp')
    }).catch(err => {
      dbg(err)
      dbg(err.response)
      throw new Error(API.getException(err) || 'Failed to init application')
    })
  },

  initSIPUA ({ commit, getters }) {
    events.mask('Loading SIPUA configuration...')
    const sipuaEnable = getters['permissions/softphoneRead']
    return API.cfgSIPUA().then(resp => {
      commit('sipua/SET_CONFIG', { conf: resp.data, sipuaEnable: sipuaEnable })
      const media = new BrowserMedia()
      if (media.isSupported) {
        media.setDevices().then(() => {
          commit('sipua/SET_BROWSER_MEDIA', media)
        })
      }
    })
  },

  initRMQ ({ dispatch, getters }) {
    events.mask('Loading RMQ agent configuration...')
    return API.cfgRMQ().then(({ data }) => {
      const opts = {
        url: data.url,
        user: getters['currentUser/currentUser'].username,
        realm: data.routing_key,
        token: Session.accessToken(),
        onStateChange: function (state) { dispatch('setRMQHint', state) },
        onMessage: function (data) { dispatch('msgConsume', data) }
      }
      RMQClient.connect(opts)
    })
  },

  reloadVmail ({ dispatch }) {
    dispatch('voicemails/init')
  },

  setRMQHint ({ commit }, hint) {
    commit('SET_RMQHINT', hint)
  },

  msgConsume ({ commit, dispatch, getters }, body) {
    const data = JSON.parse(body)
    dbg('msgConsume: %o', data)
    switch (data.action) {
      case 'PRESENCE':
        commit('users/PRESENCE_SET', data)
        break
      case 'PRESENCE_FEATURE':
        commit('settings/FEATURE_SET',
          { data: data, exten: getters['sipua/exten'] })
        commit('users/FEATURE_SET', data)
        break
      case 'REGISTER':
        commit('REGISTER_SET', data)
        break
      case 'INITIATE':
        commit('users/CALL_INITIATE', data)
        break
      case 'CONFIRMED':
        commit('users/CALL_CONFIRMED', data)
        break
      case 'CALLCHAN':
        commit('users/CALL_ASTCHAN', data)
        break
      case 'ERROR':
        events.pubError('Events error: ' + data.message)
        break
      case 'TERMINATED':
      case 'SIPERROR':
      case 'CANCELED':
        commit('users/CALL_TERMINATED', data)
        break
      case 'QUEUE':
        commit('queues/ROUTE_ACTIONS', data)
        break
      case 'QUEUECHAN':
        commit('queues/CHAN_ADD', data)
        break
      case 'CONFBRIDGE':
        commit('conferences/ROUTE_ACTIONS', data)
        break
      case 'CALLBRIDGE': // set Asterisk chan id to session
        commit('sipua/SET_SESS_CHAN', data)
        commit('users/CALL_SET_ASTCHAN', data)
        break
      case 'CHANINFO': // set Asterisk chan id to session
        commit('sipua/SET_CHANINFO', data)
        break
      case 'CHANSPY':
        commit('users/SET_BARGEIN_CALL', data)
        break
      case 'CALLMONITOR':
        commit('sipua/SET_SESS_REC', data)
        break
      case 'MWI':
        dispatch('voicemails/mwiUpdate', data)
        break
      case 'PARKING':
        dispatch('parking/update', data)
        dispatch('users/parking', data)
        dispatch('sipua/parked', data)
        break
      case 'ATTENDEDXFER':
        commit('users/ATTXFER_CALLS_SET', data)
        break
      case 'BUDDYPREF':
        commit('users/SET_PREFER', data)
        break
      default:
        break
    }
  },

  setCompactView ({ commit }, enabled) {
    commit('SET_COMPACT_VIEW', enabled)
  },

  setSuperLightView ({ commit }, enabled) {
    commit('SET_SUPER_LIGHT_VIEW', enabled)
  },

  setSuperLightSize ({ commit }, size) {
    commit('SET_SUPER_LIGHT_SIZE', size)
  },

  setBuddiesSort ({ commit }, enabled) {
    commit('SET_BUDDIES_SORT', enabled)
  },

  installCallHandler ({ getters }) {
    // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/registerProtocolHandler
    if (!getters['permissions/ctdRead']) {
      return
    }
    if (navigator?.registerProtocolHandler) {
      navigator.registerProtocolHandler('tel', `${window.location.origin}/dialer/%s`)
    }
  },

  callNumber ({ dispatch, getters }, { number, allowShow = false }) {
    const embedded = EmbeddedLocalStorage.get()
    if (embedded) {
      window.open(`sip:${number}`)
    } else {
      const softphoneRead = getters['permissions/softphoneRead']
      const ctdRead = getters['permissions/ctdRead']
      if (!ctdRead) {
        // We shouldn't be here.
        dbg('Cannot dial: click-to-dial permission missing.')
        return
      }

      const CallTypes = { FAIL: 0, SHOW: 1, DIAL: 2, ORIGINATE: 3 }
      let callType = CallTypes.UNKNOWN

      const destination = getters['settings/clickToDial']?.destination
      switch (destination) {
        default:
        case 'softphoneShow':
          if (!softphoneRead) {
            callType = CallTypes.ORIGINATE
          } else if (allowShow) {
            /*
              * Generally used for calls initiated from outside of Call Panel,
              *  such as a tel: link.
              */
            callType = CallTypes.SHOW
          } else {
            callType = CallTypes.DIAL
          }
          break
        case 'callPanelDial':
          if (!softphoneRead) {
            /*
              * Without softphone permissions, this becomes the "use tel: links" setting.
              * If we're here, it means something wasn't converted.
              */
            callType = CallTypes.FAIL
            dbg('Cannot dial: click-to-dial setting not supported when softphone permission missing.')
          } else {
            callType = CallTypes.DIAL
          }
          break
        case 'dialAfterOriginate':
          callType = CallTypes.ORIGINATE
          break
      }

      dispatch('setSIPUADialNumber', number)
      switch (callType) {
        default:
        case CallTypes.FAIL:
          return
        case CallTypes.SHOW:
          dispatch('setSIPUAPhoneDisplay', true)
          break
        case CallTypes.DIAL:
          dispatch('sipua/call', number)
          dispatch('installCallHandler')
          break
        case CallTypes.ORIGINATE:
          RMQClient.sendCmd({
            action: 'Originate',
            channel: `Local/${getters['currentUser/currentUser'].extension}@dial-number`,
            exten: `${number}`,
            priority: '1',
            context: 'default',
            callerid: number,
            amictx: 'queue',
            variable: {
              realm: getters['sipua/realm']
            }
          })
          dispatch('setSIPUADialNumber', '')
          break
      }
    }
  }
}
